// src/hooks/useChatwoot.ts
import { useAuth } from '@hooks/useAuth';
import { useEffect } from 'react';
import CryptoJS from 'crypto-js';
import {
  CHATWOOT_BASE_URL,
  CHATWOOT_SECRET,
  CHATWOOT_WEBSITE_TOKEN,
} from '@config/environment';

declare global {
  interface Window {
    chatwootSettings?: {
      position?: string;
      type?: string;
      launcherTitle?: string;
      showPopoutButton?: boolean;
      hideMessageBubble?: boolean;
      locale?: string;
      language?: string;
      availableMessage?: string;
    };
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
      setCustomer: (
        customer: any,
        message?: { message: { type: string; content: string } },
      ) => void;
    };
    $chatwoot?: {
      setUser: (
        user_id: string,
        userData: {
          email: string;
          name: string;
          avatar_url?: string;
          phone_number?: string;
          identifier_hash?: string;
        },
      ) => void;
      setCustomAttributes: (attributes: { [key: string]: any }) => void;
      reset: () => void;
      setLocale: (locale: string) => void;
    };
  }
}

export const useChatwoot = ({ enabled }: { enabled: boolean }) => {
  const { appUser } = useAuth();

  useEffect(() => {
    const scriptId = 'chatwoot-script';

    if (!enabled) {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }

      document
        .querySelectorAll('.woot-widget-holder, .woot-widget-bubble')
        .forEach((el) => el.remove());

      return;
    }

    if (document.getElementById(scriptId)) return;

    window.chatwootSettings = {
      position: 'right',
      type: 'standard',
      launcherTitle: '¿Necesitas ayuda?',
      showPopoutButton: true,
      hideMessageBubble: false,
      locale: 'es',
      language: 'es',
    };

    const g = document.createElement('script') as HTMLScriptElement;
    g.id = scriptId;
    g.src = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`;
    g.defer = true;
    g.async = true;
    document.body.appendChild(g);

    g.onload = function () {
      window.chatwootSDK?.run({
        websiteToken: CHATWOOT_WEBSITE_TOKEN,
        baseUrl: CHATWOOT_BASE_URL,
      });
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !appUser) return;

    const interval = setInterval(() => {
      if (window.$chatwoot) {
        const idHash = CryptoJS.HmacSHA256(
          appUser.id.toString(),
          CHATWOOT_SECRET,
        ).toString(CryptoJS.enc.Hex);

        window.$chatwoot.setUser(appUser.id.toString(), {
          email: appUser.email,
          name: appUser.name,
          phone_number: appUser.phone,
          identifier_hash: idHash,
        });

        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [enabled, appUser]);
};
